import { parseDate } from "@internationalized/date";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type { inferRouterOutputs } from "@trpc/server";
import { orderBy, round } from "lodash-es";
import {
  AlertTriangleIcon,
  ArrowRightLeftIcon,
  BanIcon,
  CopyIcon,
  ListOrderedIcon,
  Mail,
  PhoneIcon,
  Shirt,
  Trophy,
} from "lucide-react";
import { memo, useMemo, useState } from "react";
import {
  DialogTrigger,
  Focusable,
  TooltipTrigger,
} from "react-aria-components";

import { Button } from "@/components/base/button";
import { DataTable, type DataTableColumn } from "@/components/base/data-table";
import { Modal, ModalHeading } from "@/components/base/modal";
import { Popover } from "@/components/base/popover";
import { title } from "@/components/base/primitives";
import { SearchField } from "@/components/base/search-field";
import { addToast } from "@/components/base/toast";
import { Tooltip } from "@/components/base/tooltip";
import { ProfileName } from "@/components/profiles/name";
import { ProfilePhoto } from "@/components/profiles/photo";
import { DivisionControlsDropdown } from "@/components/teams/controls/controls-dropdown";
import { TeamControlsDropdown } from "@/components/teams/controls/dropdown";
import { ReorderWaitlist } from "@/components/teams/controls/reorder-waitlist";
import { RosterPublishedTag } from "@/components/teams/controls/roster-published-tag";
import {
  SeedDivisionColumnHeader,
  SeedDivisionModal,
} from "@/components/teams/controls/seed-division";
import { CheckInButton } from "@/components/tournaments/check-in-button";
import { useCanUpdateTournament } from "@/components/tournaments/context";
import { SanctionBadge } from "@/components/tournaments/sanction-badge";
import { TshirtSizeDisplay } from "@/components/tournaments/tshirt-size-display";
import type { TournamentDivision, TshirtSize } from "@/db/schema";
import { getLevelDisplay, getPlayerLevel } from "@/hooks/tournament";
import { useCheckIn } from "@/hooks/use-check-in";
import { useTRPC } from "@/integrations/trpc/react";
import type { tournamentsRouter } from "@/integrations/trpc/routers/tournaments";
import { getDefaultTimeZone } from "@/lib/dates";
import { formatPhone } from "@/lib/phone";
import { countNeededShirtSizes } from "@/lib/tshirt";
import { pickupCode } from "@/utils/membership";
import { playerName } from "@/utils/profiles";

type MembershipInfo = {
  id: number;
  tshirtSize: string | null;
  tshirtPickedUpAt: Date | null;
};

function TshirtSizeLabel({
  membership,
}: {
  membership: MembershipInfo | undefined;
}) {
  if (!membership) return null;
  return (
    <span className="ml-1 text-xs text-gray-400">
      [{membership.tshirtSize?.toUpperCase() ?? "?"}]
    </span>
  );
}

/**
 * Marks a team whose roster fails validation as it stands right now. An
 * unfinished admin override is the common cause, but not the only one:
 * `getInvalidRosters` re-derives every rule on read, so a profile deleted after
 * registration, a division requirement that stopped being met, or a short
 * roster flags a team that no admin ever touched. Don't read the badge as
 * "someone overrode this" — there may be no `admin_roster_edit` row behind it.
 *
 * Informational only: nothing downstream is blocked by it.
 */
function InvalidRosterBadge({ reasons }: { reasons: string[] }) {
  if (reasons.length === 0) return null;
  return (
    <TooltipTrigger delay={300}>
      <Focusable>
        <span className="ml-1 inline-flex items-center text-amber-600">
          <AlertTriangleIcon size={14} />
        </span>
      </Focusable>
      <Tooltip>
        <span className="font-medium">Invalid roster</span>
        <ul className="mt-1 list-inside list-disc">
          {reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      </Tooltip>
    </TooltipTrigger>
  );
}

function CompedBadge({ invoiceId }: { invoiceId: number | null }) {
  if (invoiceId != null) return null;
  return (
    <span className="ml-auto inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
      Comped
    </span>
  );
}

type RatingLevel = {
  abbreviated: string | null;
  name: string;
  order: number;
} | null;

/**
 * Renders a player's rating. Shows the rating they entered the tournament with
 * as an inline parenthetical, and — for completed tournaments — the level
 * earned with a trophy icon.
 */
function PlayerRating({
  enteringLevel,
  earnedLevel,
}: {
  enteringLevel: RatingLevel;
  earnedLevel: RatingLevel;
}) {
  return (
    <>
      <span
        className="ml-1 text-gray-500"
        title="Rating entering the tournament"
      >
        ({getLevelDisplay(enteringLevel)})
      </span>
      {earnedLevel && (
        <span
          className="ml-1 font-medium whitespace-nowrap text-amber-600"
          title="Rating earned at this tournament"
        >
          <Trophy
            size={12}
            className="mr-0.5 inline align-[-0.05em]"
            aria-hidden
          />
          {getLevelDisplay(earnedLevel)}
        </span>
      )}
    </>
  );
}

function EmailIcon({ email }: { email: string | undefined }) {
  if (!email) return null;

  return (
    <Button
      variant="icon"
      size="sm"
      className="mr-1.5 ml-1.5 border-gray-300 bg-transparent text-gray-400 hover:border-gray-400 hover:bg-gray-100"
      tooltip={`${email} — click to copy`}
      onPress={async () => {
        try {
          await navigator.clipboard.writeText(email);
          addToast({
            variant: "success",
            title: "Copied!",
            description: email,
          });
        } catch {
          addToast({ variant: "error", title: "Failed to copy email" });
        }
      }}
    >
      <Mail size={14} />
    </Button>
  );
}

function PhoneButton({ phoneNumber }: { phoneNumber: string | undefined }) {
  if (!phoneNumber) return null;

  return (
    <DialogTrigger>
      <Button
        variant="icon"
        size="sm"
        className="mr-1.5 ml-1.5 border-gray-300 bg-transparent text-gray-400 hover:border-gray-400 hover:bg-gray-100"
        tooltip="Phone number"
      >
        <PhoneIcon size={14} />
      </Button>
      <Popover placement="bottom" showArrow className="p-3">
        <div className="flex items-center gap-2">
          <a
            href={`tel:${phoneNumber}`}
            className="text-sm font-medium text-blue-600 hover:underline"
          >
            {formatPhone(phoneNumber)}
          </a>
          <Button
            variant="icon"
            size="sm"
            className="border-gray-300 bg-transparent text-gray-400 hover:border-gray-400 hover:bg-gray-100"
            tooltip="Copy"
            onPress={async () => {
              try {
                await navigator.clipboard.writeText(phoneNumber);
                addToast({
                  variant: "success",
                  title: "Copied!",
                  description: formatPhone(phoneNumber),
                });
              } catch {
                addToast({
                  variant: "error",
                  title: "Failed to copy phone number",
                });
              }
            }}
          >
            <CopyIcon size={12} />
          </Button>
        </div>
      </Popover>
    </DialogTrigger>
  );
}

type TshirtPlayer = {
  profile: {
    id: number;
    firstName: string;
    preferredName: string | null;
    lastName: string;
    imageSource: string | null;
  };
  membership: MembershipInfo | undefined;
};

function TshirtIcon({
  membership,
  canEdit,
  onPress,
}: {
  membership: MembershipInfo | undefined;
  canEdit: boolean;
  onPress: () => void;
}) {
  if (!canEdit) return null;

  if (!membership) {
    return (
      <Button
        variant="icon"
        size="sm"
        isDisabled
        className="mr-1.5 border-gray-200 bg-transparent text-gray-300"
        tooltip="No membership"
      >
        <Shirt size={14} />
      </Button>
    );
  }

  const isPickedUp = membership.tshirtPickedUpAt != null;
  const sizeLabel = membership.tshirtSize?.toUpperCase() ?? "?";

  return (
    <Button
      variant="icon"
      size="sm"
      className={
        isPickedUp
          ? "mr-1.5 border-green-300 bg-green-100 text-green-700 hover:border-green-400 hover:bg-green-200"
          : "mr-1.5 border-gray-300 bg-transparent text-gray-400 hover:border-gray-400 hover:bg-gray-100"
      }
      tooltip={
        isPickedUp
          ? `Shirt (${sizeLabel}) picked up`
          : `Shirt (${sizeLabel}) — click to mark picked up`
      }
      onPress={onPress}
    >
      <Shirt size={14} />
    </Button>
  );
}

function TshirtPickupConfirmModal({
  player,
  isOpen,
  onOpenChange,
  togglePickup,
  onSizeUpdated,
  isTd,
}: {
  player: TshirtPlayer | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  togglePickup: (input: { membershipId: number }) => void;
  onSizeUpdated: (membershipId: number, newSize: string) => void;
  isTd: boolean;
}) {
  if (!player?.membership) return null;
  const { profile, membership } = player;

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="flex flex-col items-center gap-4 p-6">
        <ModalHeading size="md">T-Shirt Pickup</ModalHeading>

        <ProfilePhoto
          imageSource={profile.imageSource}
          preferredName={profile.preferredName}
          firstName={profile.firstName}
          lastName={profile.lastName}
          className="h-24 w-24"
        />

        <div className="text-xl font-semibold">{playerName(profile)}</div>

        <TshirtSizeDisplay
          membershipId={membership.id}
          tshirtSize={membership.tshirtSize}
          tshirtPickedUpAt={membership.tshirtPickedUpAt}
          onSizeChange={onSizeUpdated}
          onTogglePickup={() => togglePickup({ membershipId: membership.id })}
          isTd={isTd}
        />
      </div>
    </Modal>
  );
}

export const TeamsPanel = memo(function TeamsPanel({
  tournamentDivisionId,
  teamSize,
  isActive,
  date,
}: {
  tournamentDivisionId: TournamentDivision["id"];
  teamSize: TournamentDivision["teamSize"];
  isActive: boolean;
  date: string;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { tournamentId } = useParams({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });
  const [search, setSearch] = useState("");
  const [tshirtPlayer, setTshirtPlayer] = useState<TshirtPlayer | null>(null);
  const [isSeedModalOpen, setIsSeedModalOpen] = useState(false);

  const canEdit = useCanUpdateTournament();

  const teamsQueryOptions = trpc.tournaments.getTeams.queryOptions({
    tournamentDivisionId,
  });

  const withdrawnTeamsQueryOptions =
    trpc.tournaments.getWithdrawnTeams.queryOptions({
      tournamentDivisionId,
    });

  const teamStatsQueryOptions =
    trpc.tournaments.getTournamentTeamStats.queryOptions({
      tournamentId: Number(tournamentId),
    });

  const { data } = useQuery({ ...teamsQueryOptions, enabled: isActive });

  // Stagger TD-only queries: wait for getTeams to complete before firing these
  // to avoid exhausting the DB connection pool with too many concurrent queries
  const teamsLoaded = data != null;
  const { details, detailsQueryOptions, checkInMap, toggleCheckIn } =
    useCheckIn({
      tournamentDivisionId,
      enabled: isActive && canEdit && teamsLoaded,
    });
  const { data: withdrawnTeams } = useQuery({
    ...withdrawnTeamsQueryOptions,
    enabled: isActive && canEdit && teamsLoaded,
  });
  const { data: teamStats } = useQuery({
    ...teamStatsQueryOptions,
    enabled: isActive && canEdit && teamsLoaded,
  });

  // Rosters an admin left invalid via an acknowledged override. Derived on
  // read, so the flag clears itself the moment the roster is fixed — there is
  // nothing to dismiss. Its own query rather than a field on `getTeams`, which
  // is public and hot.
  const { data: invalidRosters } = useQuery({
    ...trpc.tournaments.getInvalidRosters.queryOptions({
      tournamentDivisionId,
    }),
    enabled: isActive && canEdit && teamsLoaded,
  });

  const invalidRosterMap = useMemo(
    () =>
      new Map(
        invalidRosters?.map((r) => [
          r.tournamentDivisionTeamId,
          r.violations,
        ]) ?? [],
      ),
    [invalidRosters],
  );

  const { membershipMap, abandonedRefMap, emailMap, phoneMap } = useMemo(
    () => ({
      membershipMap: new Map(
        details?.memberships.map((m) => [m.profileId, m]) ?? [],
      ),
      abandonedRefMap: new Map(
        details?.abandonedRefs.map((r) => [r.teamId, r.refId]) ?? [],
      ),
      emailMap: new Map(details?.emails.map((e) => [e.userId, e.email]) ?? []),
      phoneMap: new Map(
        details?.phoneNumbers.map((p) => [p.userId, p.phoneNumber]) ?? [],
      ),
    }),
    [details],
  );

  const playerContact = (profile: { userId: string | null }) => ({
    email: profile.userId ? emailMap.get(profile.userId) : undefined,
    phone: profile.userId ? phoneMap.get(profile.userId) : undefined,
  });

  const { mutate: togglePickup } = useMutation({
    mutationFn:
      trpc.tournaments.markTshirtPickedUp.mutationOptions().mutationFn,
    onMutate: async (input) => {
      await queryClient.cancelQueries(detailsQueryOptions);
      const previous = queryClient.getQueryData(detailsQueryOptions.queryKey);

      queryClient.setQueryData(
        detailsQueryOptions.queryKey,
        (old: typeof details) => {
          if (!old) return old;
          return {
            ...old,
            memberships: old.memberships.map((m) =>
              m.id !== input.membershipId
                ? m
                : {
                    ...m,
                    tshirtPickedUpAt: m.tshirtPickedUpAt ? null : new Date(),
                  },
            ),
          };
        },
      );

      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          detailsQueryOptions.queryKey,
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(detailsQueryOptions);
      setTshirtPlayer(null);
    },
  });

  const { mutate: updateTshirtSize } = useMutation({
    mutationFn: trpc.tournaments.updateTshirtSize.mutationOptions().mutationFn,
    onMutate: async (input) => {
      await queryClient.cancelQueries(detailsQueryOptions);
      const previous = queryClient.getQueryData(detailsQueryOptions.queryKey);

      queryClient.setQueryData(
        detailsQueryOptions.queryKey,
        (old: typeof details) => {
          if (!old) return old;
          return {
            ...old,
            memberships: old.memberships.map((m) =>
              m.id !== input.membershipId
                ? m
                : { ...m, tshirtSize: input.tshirtSize },
            ),
          };
        },
      );

      // Also update tshirtPlayer state so the modal reflects the change
      setTshirtPlayer((prev) => {
        if (!prev?.membership || prev.membership.id !== input.membershipId)
          return prev;
        return {
          ...prev,
          membership: { ...prev.membership, tshirtSize: input.tshirtSize },
        };
      });

      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          detailsQueryOptions.queryKey,
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(detailsQueryOptions);
    },
  });

  const filteredData = useMemo(() => {
    if (!data) return null;
    if (!search.trim() || !canEdit) return data;

    const query = search.toLowerCase();
    return data.filter((team) =>
      team.team.players.some((tp) => {
        const name = playerName(tp.profile).toLowerCase();
        if (name.includes(query)) return true;
        const m = membershipMap.get(tp.profile.id);
        if (m && pickupCode(m.id).includes(query)) return true;
        return false;
      }),
    );
  }, [data, search, canEdit, membershipMap]);

  const activeProfileIds = useMemo(() => {
    if (!canEdit || !data) return new Set<number>();
    return new Set(
      data
        .filter((t) => t.status !== "waitlisted")
        .flatMap((t) => t.team.players.map((tp) => tp.profile.id)),
    );
  }, [canEdit, data]);

  const allProfileIds = useMemo(() => {
    if (!canEdit || !data) return new Set<number>();
    return new Set(
      data.flatMap((t) => t.team.players.map((tp) => tp.profile.id)),
    );
  }, [canEdit, data]);

  const totalPlayerCount = canEdit ? activeProfileIds.size : 0;

  const checkInCount =
    canEdit && details
      ? details.checkIns.filter((c) => activeProfileIds.has(c.playerProfileId))
          .length
      : 0;

  const { pickupCount, membershipCount, sortedSizeCounts } = useMemo(() => {
    if (!canEdit || !details)
      return {
        pickupCount: 0,
        membershipCount: 0,
        sortedSizeCounts: [] as [TshirtSize, number][],
      };

    const relevant = details.memberships.filter((m) =>
      allProfileIds.has(m.profileId),
    );
    return {
      membershipCount: relevant.length,
      pickupCount: relevant.filter((m) => m.tshirtPickedUpAt != null).length,
      // "Shirts needed" reflects only shirts not yet handed out, ordered by size.
      sortedSizeCounts: countNeededShirtSizes(relevant),
    };
  }, [canEdit, details, allProfileIds]);

  if (!filteredData) {
    return (
      <div className="mx-auto flex max-w-4xl flex-col gap-3 px-3 py-12">
        <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg bg-gray-100"
            />
          ))}
        </div>
      </div>
    );
  }

  const activeTeams = orderBy(
    filteredData.filter(({ status }) => status !== "waitlisted"),
    ["finish", "seed", "order", "createdAt"],
    ["asc", "asc", "asc", "asc"],
  );

  const waitlist = orderBy(
    filteredData.filter(({ status }) => status === "waitlisted"),
    ["order", "createdAt"],
    ["asc", "asc"],
  );

  const divisionStats = teamStats?.find(
    (s) => s.divisionId === tournamentDivisionId,
  );

  type Team = (typeof filteredData)[number];

  const hasFinish = activeTeams.some((t) => t.finish != null || t.forfeited);
  const hasSeed = activeTeams.some((t) => t.seed != null);
  const hasAwardedRatings = activeTeams.some(
    (t) => t.levelEarnedId != null || t.pointsEarned != null,
  );

  // Player row shared by the mobile expanded views: name, rating, and (for
  // TDs) contact icons.
  function playerDetailRow(
    team: Team,
    tp: Team["team"]["players"][number],
    i: number,
  ): [string, React.ReactNode] {
    const { email, phone } = playerContact(tp.profile);
    return [
      `Player ${i + 1}`,
      <span key={tp.profile.id} className="inline-flex items-center">
        <ProfileName {...tp.profile} />
        <PlayerRating
          enteringLevel={getPlayerLevel(tp)}
          earnedLevel={team.levelEarned}
        />
        {canEdit && <EmailIcon email={email} />}
        {canEdit && <PhoneButton phoneNumber={phone} />}
      </span>,
    ];
  }

  function renderFinish(team: Team) {
    if (team.forfeited) {
      return (
        <TooltipTrigger delay={300}>
          <Focusable>
            <span className="inline-flex size-6 items-center justify-center rounded-full bg-red-100 text-xs font-bold text-red-600">
              F
            </span>
          </Focusable>
          <Tooltip>Forfeit</Tooltip>
        </TooltipTrigger>
      );
    }
    return team.finish ?? "-";
  }

  // -- Mobile columns --

  const mobileColumns: DataTableColumn<Team>[] = [
    ...(hasFinish
      ? [
          {
            id: "finish",
            header: "",
            width: 50,
            cell: (team: Team) => renderFinish(team),
          },
        ]
      : hasSeed
        ? [
            {
              id: "seed",
              header: "",
              width: 50,
              cell: (team: Team) => team.seed ?? "-",
            },
          ]
        : []),
    {
      id: "players",
      header: "Players",
      isRowHeader: true,
      cell: (team) => (
        <div className="flex flex-wrap items-center">
          {team.team.players.map((tp, i) => {
            const { profile } = tp;
            const membership = membershipMap.get(profile.id);
            return (
              <span key={profile.id} className="inline-flex items-center">
                {canEdit && (
                  <CheckInButton
                    checkIn={checkInMap.get(profile.id)}
                    playerProfileId={profile.id}
                    toggleCheckIn={toggleCheckIn}
                  />
                )}
                <TshirtIcon
                  membership={membership}
                  canEdit={canEdit}
                  onPress={() => setTshirtPlayer({ profile, membership })}
                />
                <ProfileName {...profile} />
                <PlayerRating
                  enteringLevel={getPlayerLevel(tp)}
                  earnedLevel={team.levelEarned}
                />
                {i < team.team.players.length - 1 && " / "}
              </span>
            );
          })}
          {canEdit && (
            <InvalidRosterBadge
              reasons={(invalidRosterMap.get(team.id) ?? []).map(
                (v) => v.message,
              )}
            />
          )}
          {canEdit && <CompedBadge invoiceId={team.invoiceId} />}{" "}
        </div>
      ),
    },
    ...(canEdit
      ? [
          {
            id: "actions",
            header: "",
            width: 48,
            cell: (team: Team) => (
              <TeamControlsDropdown
                tournamentDivisionTeamId={team.id}
                status={team.status}
                seed={team.seed}
                poolTeam={team.poolTeam ?? undefined}
                abandonedRefId={abandonedRefMap.get(team.id)}
                forfeited={team.forfeited}
              />
            ),
          },
        ]
      : []),
  ];

  const mobileWaitlistColumns: DataTableColumn<Team>[] = [
    {
      id: "players",
      header: "Players",
      isRowHeader: true,
      cell: (team) => (
        <div className="flex flex-wrap items-center">
          {team.team.players.map((tp, i) => {
            const { profile } = tp;
            const membership = membershipMap.get(profile.id);
            return (
              <span key={profile.id} className="inline-flex items-center">
                <TshirtIcon
                  membership={membership}
                  canEdit={canEdit}
                  onPress={() => setTshirtPlayer({ profile, membership })}
                />
                <ProfileName {...profile} />
                <PlayerRating
                  enteringLevel={getPlayerLevel(tp)}
                  earnedLevel={team.levelEarned}
                />
                {i < team.team.players.length - 1 && " / "}
              </span>
            );
          })}
          {canEdit && (
            <InvalidRosterBadge
              reasons={(invalidRosterMap.get(team.id) ?? []).map(
                (v) => v.message,
              )}
            />
          )}
          {canEdit && <CompedBadge invoiceId={team.invoiceId} />}{" "}
        </div>
      ),
    },
    ...(canEdit
      ? [
          {
            id: "actions",
            header: "",
            width: 48,
            cell: (team: Team) => (
              <TeamControlsDropdown
                tournamentDivisionTeamId={team.id}
                status="waitlisted"
                seed={team.seed}
                poolTeam={team.poolTeam ?? undefined}
                abandonedRefId={abandonedRefMap.get(team.id)}
              />
            ),
          },
        ]
      : []),
  ];

  function renderMobileExpanded(team: Team) {
    const details: [string, React.ReactNode][] = [
      ["Finish", renderFinish(team)],
      ["Seed", team.seed ?? "-"],
      [
        "Pool",
        <span key="pool" className="uppercase">
          {team.poolTeam?.pool.name ?? "-"}
        </span>,
      ],
      ...(team.pointsEarned != null
        ? [
            ["Points earned", round(team.pointsEarned)] as [
              string,
              React.ReactNode,
            ],
          ]
        : []),
      ...team.team.players.map((tp, i) => playerDetailRow(team, tp, i)),
    ];

    return (
      <div className="flex flex-col text-sm">
        {details.map(([label, value]) => (
          <div
            key={label}
            className="flex items-center justify-between px-2 py-1.5 odd:bg-gray-100"
          >
            <span className="font-medium">{label}</span>
            <span>{value}</span>
          </div>
        ))}
      </div>
    );
  }

  function renderMobileWaitlistExpanded(team: Team) {
    const details: [string, React.ReactNode][] = team.team.players.map(
      (tp, i) => playerDetailRow(team, tp, i),
    );

    return (
      <div className="flex flex-col text-sm">
        {details.map(([label, value]) => (
          <div
            key={label}
            className="flex items-center justify-between px-2 py-1.5 odd:bg-gray-100"
          >
            <span className="font-medium">{label}</span>
            <span>{value}</span>
          </div>
        ))}
      </div>
    );
  }

  // -- Desktop columns (full detail) --

  const desktopColumns: DataTableColumn<Team>[] = [
    {
      id: "finish",
      header: "Finish",
      width: 70,
      cell: (team) => renderFinish(team),
    },
    {
      id: "seed",
      header:
        canEdit && divisionStats ? (
          <SeedDivisionColumnHeader
            hasSeed={hasSeed}
            onPress={() => setIsSeedModalOpen(true)}
          />
        ) : (
          "Seed"
        ),
      isRowHeader: true,
      width: 70,
      cell: (team) => team.seed ?? "-",
    },
    {
      id: "pool",
      header: "Pool",
      width: 70,
      cell: (team) => (
        <span className="uppercase">{team.poolTeam?.pool.name ?? "-"}</span>
      ),
    },
    ...Array.from({ length: teamSize }, (_, i) => ({
      id: `player-${i}`,
      header: `Player ${i + 1}`,
      isRowHeader: true,
      cell: (team: Team) => {
        const player = team.team.players[i];
        // The flag rides on the first player column so it stays visible even
        // when the override left the roster short or empty.
        const invalidBadge = canEdit && i === 0 && (
          <InvalidRosterBadge
            reasons={(invalidRosterMap.get(team.id) ?? []).map(
              (v) => v.message,
            )}
          />
        );
        if (!player) {
          return i === teamSize - 1 && canEdit && team.invoiceId == null ? (
            <span className="flex justify-end">
              {invalidBadge}
              <CompedBadge invoiceId={team.invoiceId} />
            </span>
          ) : (
            <span className="inline-flex items-center">
              {"-"}
              {invalidBadge}
            </span>
          );
        }
        const membership = membershipMap.get(player.profile.id);
        const { email, phone } = playerContact(player.profile);
        const isLast = canEdit && i === teamSize - 1;
        return (
          <span
            className={
              isLast ? "flex w-full items-center" : "inline-flex items-center"
            }
          >
            {canEdit && (
              <CheckInButton
                checkIn={checkInMap.get(player.profile.id)}
                playerProfileId={player.profile.id}
                toggleCheckIn={toggleCheckIn}
              />
            )}
            <TshirtIcon
              membership={membership}
              canEdit={canEdit}
              onPress={() =>
                setTshirtPlayer({ profile: player.profile, membership })
              }
            />
            <ProfileName {...player.profile} />
            <PlayerRating
              enteringLevel={getPlayerLevel(player)}
              earnedLevel={team.levelEarned}
            />
            {canEdit && <EmailIcon email={email} />}
            {canEdit && <PhoneButton phoneNumber={phone} />}
            <TshirtSizeLabel membership={membership} />
            {invalidBadge}
            {isLast && <CompedBadge invoiceId={team.invoiceId} />}
          </span>
        );
      },
    })),
    ...(hasAwardedRatings
      ? [
          {
            id: "points",
            header: "Pts",
            width: 70,
            cell: (team: Team) =>
              team.pointsEarned != null ? round(team.pointsEarned) : "-",
          },
        ]
      : []),
    ...(canEdit
      ? [
          {
            id: "actions",
            header: "",
            width: 48,
            cell: (team: Team) => (
              <TeamControlsDropdown
                tournamentDivisionTeamId={team.id}
                status={team.status}
                seed={team.seed}
                poolTeam={team.poolTeam ?? undefined}
                abandonedRefId={abandonedRefMap.get(team.id)}
                forfeited={team.forfeited}
              />
            ),
          },
        ]
      : []),
  ];

  const desktopWaitlistColumns: DataTableColumn<Team>[] = [
    ...Array.from({ length: teamSize }, (_, i) => ({
      id: `player-${i}`,
      header: `Player ${i + 1}`,
      isRowHeader: true,
      cell: (team: Team) => {
        const player = team.team.players[i];
        // The flag rides on the first player column so it stays visible even
        // when the override left the roster short or empty.
        const invalidBadge = canEdit && i === 0 && (
          <InvalidRosterBadge
            reasons={(invalidRosterMap.get(team.id) ?? []).map(
              (v) => v.message,
            )}
          />
        );
        if (!player) {
          return i === teamSize - 1 && canEdit && team.invoiceId == null ? (
            <span className="flex justify-end">
              {invalidBadge}
              <CompedBadge invoiceId={team.invoiceId} />
            </span>
          ) : (
            <span className="inline-flex items-center">
              {"-"}
              {invalidBadge}
            </span>
          );
        }
        const membership = membershipMap.get(player.profile.id);
        const { email, phone } = playerContact(player.profile);
        const isLast = canEdit && i === teamSize - 1;
        return (
          <span
            className={
              isLast ? "flex w-full items-center" : "inline-flex items-center"
            }
          >
            <TshirtIcon
              membership={membership}
              canEdit={canEdit}
              onPress={() =>
                setTshirtPlayer({ profile: player.profile, membership })
              }
            />
            <ProfileName {...player.profile} />
            <PlayerRating
              enteringLevel={getPlayerLevel(player)}
              earnedLevel={team.levelEarned}
            />
            {canEdit && <EmailIcon email={email} />}
            {canEdit && <PhoneButton phoneNumber={phone} />}
            <TshirtSizeLabel membership={membership} />
            {invalidBadge}
            {isLast && <CompedBadge invoiceId={team.invoiceId} />}
          </span>
        );
      },
    })),
    ...(canEdit
      ? [
          {
            id: "actions",
            header: "",
            width: 48,
            cell: (team: Team) => (
              <TeamControlsDropdown
                tournamentDivisionTeamId={team.id}
                status="waitlisted"
                seed={team.seed}
                poolTeam={team.poolTeam ?? undefined}
                abandonedRefId={abandonedRefMap.get(team.id)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-3 px-3 py-12">
      {canEdit && divisionStats && (
        <SeedDivisionModal
          tournamentId={Number(tournamentId)}
          tournamentDivisionId={tournamentDivisionId}
          divisionName={divisionStats.divisionName}
          isOpen={isSeedModalOpen}
          onOpenChange={setIsSeedModalOpen}
          showAutopromoteWarning={
            parseDate(date).toDate(getDefaultTimeZone()).getTime() -
              Date.now() >
            24 * 60 * 60 * 1000
          }
        />
      )}
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm">
              {activeTeams.length} team{activeTeams.length !== 1 ? "s" : ""}
              {waitlist.length > 0 && ` (${waitlist.length} waitlisted)`}
            </span>
            {canEdit && divisionStats && (
              <SanctionBadge
                sanction={divisionStats.sanction}
                divisionName={divisionStats.divisionName}
              />
            )}
            {canEdit && totalPlayerCount > 0 && (
              <span className="text-sm text-gray-500">
                {checkInCount}/{totalPlayerCount} checked in
              </span>
            )}
            {canEdit && membershipCount > 0 && (
              <span className="text-sm text-gray-500">
                {pickupCount}/{membershipCount} shirts picked up
              </span>
            )}
            {canEdit && <RosterPublishedTag />}
          </div>
          {canEdit && sortedSizeCounts.length > 0 && (
            <span className="text-sm text-gray-500">
              Shirts needed:{" "}
              {sortedSizeCounts
                .map(([size, count]) => `${size.toUpperCase()} x${count}`)
                .join(", ")}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          {canEdit && (
            <div className="flex items-center gap-2">
              <SearchField
                aria-label="Search players"
                placeholder="Name or pickup code"
                value={search}
                onChange={setSearch}
                className="w-full md:w-64"
              />
              <DivisionControlsDropdown
                tournamentId={Number.parseInt(tournamentId, 10)}
                tournamentDivisionId={tournamentDivisionId}
                hasSeed={hasSeed}
                onSeedPress={() => setIsSeedModalOpen(true)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden">
        <DataTable
          aria-label="Teams"
          data={activeTeams}
          columns={mobileColumns}
          getRowId={(t) => t.id}
          renderExpandedContent={renderMobileExpanded}
          renderEmptyState={() => (
            <div className="w-full p-2 text-center">No teams to show.</div>
          )}
        />
      </div>

      {/* Desktop */}
      <div className="hidden md:block">
        <DataTable
          aria-label="Teams"
          data={activeTeams}
          columns={desktopColumns}
          getRowId={(t) => t.id}
          renderEmptyState={() => (
            <div className="w-full p-2 text-center">No teams to show.</div>
          )}
        />
      </div>

      {canEdit && waitlist.length > 0 && (
        <div className="mt-6 flex flex-col space-y-3">
          <div className="flex items-center justify-between">
            <h3 className={title({ size: "sm" })}>Waitlist</h3>
            {waitlist.length > 1 && (
              <ReorderWaitlist
                divisionId={tournamentDivisionId}
                trigger={
                  <Button variant="icon" color="secondary" tooltip="Reorder">
                    <ListOrderedIcon size={16} />
                  </Button>
                }
              />
            )}
          </div>

          {/* Mobile */}
          <div className="md:hidden">
            <DataTable
              aria-label="Waitlisted Teams"
              data={waitlist}
              columns={mobileWaitlistColumns}
              getRowId={(t) => t.id}
              renderExpandedContent={renderMobileWaitlistExpanded}
            />
          </div>

          {/* Desktop */}
          <div className="hidden md:block">
            <DataTable
              aria-label="Waitlisted Teams"
              data={waitlist}
              columns={desktopWaitlistColumns}
              getRowId={(t) => t.id}
            />
          </div>
        </div>
      )}

      {canEdit && withdrawnTeams && withdrawnTeams.length > 0 && (
        <WithdrawnTeamsSection
          withdrawnTeams={withdrawnTeams}
          teamSize={teamSize}
          showRefunds
        />
      )}

      <TshirtPickupConfirmModal
        player={tshirtPlayer}
        isOpen={tshirtPlayer != null}
        onOpenChange={(open) => {
          if (!open) setTshirtPlayer(null);
        }}
        togglePickup={togglePickup}
        onSizeUpdated={(membershipId, newSize) => {
          updateTshirtSize({ membershipId, tshirtSize: newSize });
        }}
        isTd={canEdit}
      />
    </div>
  );
});

type WithdrawnTeam = inferRouterOutputs<
  typeof tournamentsRouter
>["getWithdrawnTeams"][number];

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

// Refund column precedence: executed amount > Failed (dead-lettered, needs a
// human) > Pending (owed, not yet run) > None (nothing owed).
function renderRefund(team: WithdrawnTeam): React.ReactNode {
  if (team.refunded) return currencyFormatter.format(team.refundAmount);
  if (team.refundFailed) {
    return (
      <TooltipTrigger delay={300}>
        <Focusable>
          <span className="text-xs font-medium text-red-600">Failed</span>
        </Focusable>
        <Tooltip>Refund could not be processed — contact support.</Tooltip>
      </TooltipTrigger>
    );
  }
  if (team.refundPending)
    return <span className="text-xs text-amber-600">Pending</span>;
  return "None";
}

function WithdrawnTeamsSection({
  withdrawnTeams,
  teamSize,
  showRefunds,
}: {
  withdrawnTeams: WithdrawnTeam[];
  teamSize: number;
  showRefunds: boolean;
}) {
  const mobileColumns: DataTableColumn<WithdrawnTeam>[] = [
    {
      id: "players",
      header: "Players",
      isRowHeader: true,
      cell: (team) => (
        <span>
          {team.team.players.map((tp, i) => (
            <span key={tp.profile.id} className="inline-flex items-center">
              <ProfileName {...tp.profile} />
              <PlayerRating
                enteringLevel={getPlayerLevel(tp)}
                earnedLevel={null}
              />
              {i < team.team.players.length - 1 && " / "}
            </span>
          ))}
        </span>
      ),
    },
    {
      id: "type",
      header: "Type",
      width: 48,
      cell: (team) =>
        team.status === "late-withdraw" ? (
          <AlertTriangleIcon size={14} className="text-amber-500" />
        ) : team.status === "transferred" ? (
          <ArrowRightLeftIcon size={14} className="text-blue-500" />
        ) : team.status === "cancelled" ? (
          <BanIcon size={14} className="text-red-500" />
        ) : null,
    },
  ];

  const desktopColumns: DataTableColumn<WithdrawnTeam>[] = [
    ...Array.from({ length: teamSize }, (_, i) => ({
      id: `player-${i}`,
      header: `Player ${i + 1}`,
      isRowHeader: i === 0,
      cell: (team: WithdrawnTeam) => {
        const player = team.team.players[i];
        if (!player) return "-";
        return (
          <span className="inline-flex items-center">
            <ProfileName {...player.profile} />
            <PlayerRating
              enteringLevel={getPlayerLevel(player)}
              earnedLevel={null}
            />
          </span>
        );
      },
    })),
    {
      id: "withdrawn-at",
      header: "Dropped",
      width: 140,
      cell: (team) =>
        team.withdrawnAt
          ? dateFormatter.format(new Date(team.withdrawnAt))
          : "-",
    },
    {
      id: "withdrawn-by",
      header: "Dropped By",
      width: 120,
      cell: (team) => (
        <span className="text-xs text-gray-500">{team.withdrawnBy ?? "—"}</span>
      ),
    },
    {
      id: "type",
      header: "Type",
      width: 80,
      cell: (team) =>
        team.status === "late-withdraw" ? (
          <span className="flex items-center gap-1 text-xs font-medium text-amber-600">
            <AlertTriangleIcon size={14} /> Late
          </span>
        ) : team.status === "transferred" ? (
          <span className="flex items-center gap-1 text-xs font-medium text-blue-600">
            <ArrowRightLeftIcon size={14} /> Transfer
          </span>
        ) : team.status === "cancelled" ? (
          <span className="flex items-center gap-1 text-xs font-medium text-red-600">
            <BanIcon size={14} /> Cancelled
          </span>
        ) : (
          <span className="text-xs text-gray-500">Drop</span>
        ),
    },
    {
      id: "from",
      header: "From",
      width: 90,
      cell: (team) =>
        team.fromWaitlist === true ? (
          <span className="text-xs text-gray-500">Waitlist</span>
        ) : team.fromWaitlist === false ? (
          <span className="text-xs text-gray-500">Active</span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    ...(showRefunds
      ? [
          {
            id: "refunded",
            header: "Refund",
            width: 100,
            cell: (team: WithdrawnTeam) => renderRefund(team),
          },
        ]
      : []),
  ];

  function renderMobileExpanded(team: WithdrawnTeam) {
    const details: [string, React.ReactNode][] = [
      ...team.team.players.map((tp, i): [string, React.ReactNode] => [
        `Player ${i + 1}`,
        <span key={tp.profile.id} className="inline-flex items-center">
          <ProfileName {...tp.profile} />
          <PlayerRating enteringLevel={getPlayerLevel(tp)} earnedLevel={null} />
        </span>,
      ]),
      [
        "Dropped",
        team.withdrawnAt
          ? dateFormatter.format(new Date(team.withdrawnAt))
          : "-",
      ],
      [
        "Type",
        team.status === "late-withdraw"
          ? "Late Drop"
          : team.status === "transferred"
            ? "Transfer"
            : team.status === "cancelled"
              ? "Cancelled"
              : "Drop",
      ],
      [
        "From",
        team.fromWaitlist === true
          ? "Waitlist"
          : team.fromWaitlist === false
            ? "Active"
            : "—",
      ],
      ["Dropped By", team.withdrawnBy ?? "—"],
      ...(showRefunds
        ? [["Refund", renderRefund(team)] as [string, React.ReactNode]]
        : []),
    ];

    return (
      <div className="flex flex-col text-sm">
        {details.map(([label, value]) => (
          <div
            key={label}
            className="flex items-center justify-between px-2 py-1.5 odd:bg-gray-100"
          >
            <span className="font-medium">{label}</span>
            <span>{value}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-6 flex flex-col space-y-3">
      <h3 className={title({ size: "sm" })}>
        Withdrawn ({withdrawnTeams.length})
      </h3>

      {/* Mobile */}
      <div className="md:hidden">
        <DataTable
          aria-label="Withdrawn Teams"
          data={withdrawnTeams}
          columns={mobileColumns}
          getRowId={(t) => t.id}
          renderExpandedContent={renderMobileExpanded}
        />
      </div>

      {/* Desktop */}
      <div className="hidden md:block">
        <DataTable
          aria-label="Withdrawn Teams"
          data={withdrawnTeams}
          columns={desktopColumns}
          getRowId={(t) => t.id}
        />
      </div>
    </div>
  );
}
